import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { trigger, transition, style, animate } from "@angular/animations";
import { toSignal } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";
import { AvatarModule } from "primeng/avatar";
import { SkeletonModule } from "primeng/skeleton";

import { KbdShortcutComponent } from "src/app/shared/components/kbd-shortcut/kbd-shortcut.component";
import { KEYBOARD_SHORTCUTS } from "src/app/shared/constants/keyboard-shortcuts";
import { selectUserContext } from "src/app/store/user/user.selectors";
import { selectNavigationView, selectHasNavigated, selectSidebarExpanded } from "src/app/store/navigation/navigation.selectors";
import { NavigationActions } from "src/app/store/navigation/navigation.actions";
import { NavRoutesService } from "src/app/navigation/nav-routes.service";
import { HomeViewComponent } from "./views/home/home-view.component";
import { QuoteViewComponent } from "./views/quote/quote-view.component";
import { CustomerViewComponent } from "./views/customer/customer-view.component";
import { ContactViewComponent } from "./views/contact/contact-view.component";
import { ContractViewComponent } from "./views/contract/contract-view.component";
import { NotificationBellComponent } from "src/app/navigation/navigation-header/views/home/components/notification-bell/notification-bell.component";

@Component({
    selector: "app-side-navigation",
    templateUrl: "./side-navigation.component.html",
    styleUrls: ["./side-navigation.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger("navViewSwitch", [
            transition("* => *", [
                style({ opacity: 0, transform: "translateX(12px)" }),
                animate("160ms ease-out", style({ opacity: 1, transform: "none" })),
            ]),
        ]),
    ],
    imports: [
        RouterModule,
        TranslateModule,
        TooltipModule,
        AvatarModule,
        SkeletonModule,
        HomeViewComponent,
        QuoteViewComponent,
        CustomerViewComponent,
        ContactViewComponent,
        ContractViewComponent,
        KbdShortcutComponent,
        NotificationBellComponent,
    ],
})
export class SideNavigationComponent {
    public readonly SHORTCUTS = KEYBOARD_SHORTCUTS;

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly navRoutes = inject(NavRoutesService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly expanded = toSignal(this.store.select(selectSidebarExpanded), { initialValue: true });
    public readonly user = toSignal(this.store.select(selectUserContext), { initialValue: null });
    public readonly activeView = toSignal(this.store.select(selectNavigationView), { initialValue: "main" as const });
    public readonly hasNavigated = toSignal(this.store.select(selectHasNavigated), { initialValue: false });

    // Pre-allocated array for the skeleton rows (avoids creating arrays each render).
    public readonly skeletonRows = Array.from({ length: 6 });

    // Disable the view-switch animation when the user prefers reduced motion.
    public readonly reducedMotion = signal(matchMedia("(prefers-reduced-motion: reduce)").matches);

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly homeRoute = computed<string[]>(() => this.navRoutes.route("quote"));

    public readonly initials = computed(() => {
        const u = this.user();
        if (!u?.name) return "?";
        const names = u.name.split(" ");
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
        }
        return names[0][0]?.toUpperCase() || "?";
    });

    public readonly profileTooltip = computed(() => {
        const u = this.user();
        return [u?.name, u?.selectedCompany?.name?.toUpperCase()].filter(Boolean).join("<br>");
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public toggle(): void {
        this.store.dispatch(NavigationActions.setSidebarExpanded({ expanded: !this.expanded() }));
    }

    public onToggleSettingsDrawer(): void {
        this.store.dispatch(NavigationActions.toggleSettingsDrawer());
    }
}
